import {
  Alert,
  Box,
  MenuItem,
  Select as MuiSelect,
  Stack
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import TextFilter from 'components/TextFilter'
import EmptyScreen from 'packages/ui/EmptyScreen'
import TableView, { type IItem, useTable } from 'packages/ui/Table'

import { type DeliverySourceType } from '../../types'
import CountBadge from '../CountBadge'
import COLUMNS_CONFIG from './columns-config'
import useEmailSends from './use-email-sends'

type SourceTypeFilter = DeliverySourceType | 'all'

type SendsTableProps = {
  initialSourceType?: DeliverySourceType | null
  initialSourceId?: string | null
  showFilters?: boolean
}

const SendsTable = ({
  initialSourceType = null,
  initialSourceId = null,
  showFilters = true
}: SendsTableProps) => {
  const { t } = useTranslation()
  const [verbatim, setVerbatim] = useState('')
  const [sourceType, setSourceType] = useState<DeliverySourceType | null>(initialSourceType)
  const emailSends = useEmailSends({
    verbatim,
    sourceType,
    sourceId: initialSourceId
  })
  const apiTable = useTable({
    rowKey: 'id',
    loading: emailSends.loading,
    loadingNext: emailSends.loadingNext,
    items: emailSends.items as unknown as IItem[],
    columns: COLUMNS_CONFIG
  })
  const selectedSourceType: SourceTypeFilter = sourceType ?? 'all'

  const handleSourceTypeChange = (nextSourceType: SourceTypeFilter) => {
    setSourceType(nextSourceType === 'all' ? null : nextSourceType)
  }

  const renderedTable = useMemo(() => {
    if (emailSends.loading) {
      return <TableSkeleton />
    }

    if (emailSends.error) {
      return (
        <Stack justifyContent="center" sx={{ p: 2 }}>
          <Alert severity="error" sx={{ borderRadius: '16px' }}>
            {t('mailing.sends.error_list')}
          </Alert>
        </Stack>
      )
    }

    if (!emailSends.items.length) {
      return (
        <EmptyScreen icon="envelope" variant="compact">
          {t('mailing.sends.empty_list')}
        </EmptyScreen>
      )
    }

    return <TableView apiTable={apiTable} />
  }, [apiTable, emailSends.error, emailSends.items.length, emailSends.loading])

  return (
    <Stack gap={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent={showFilters ? 'space-between' : 'flex-end'}
        gap={2}
        sx={{ flexShrink: 0 }}
      >
        {showFilters && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={1}
          >
            <Box sx={{ width: { xs: '100%', sm: 280 } }}>
              <TextFilter
                value={verbatim}
                placeholder={t('mailing.sends.search_placeholder')}
                onChange={setVerbatim}
                width="100%"
              />
            </Box>

            <MuiSelect
              value={selectedSourceType}
              onChange={(event) => handleSourceTypeChange(event.target.value as SourceTypeFilter)}
              size="small"
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    borderRadius: '8px',
                    '& .MuiMenuItem-root': {
                      minHeight: 32,
                      py: 0.5,
                      fontSize: 12,
                      fontWeight: 500
                    }
                  }
                }
              }}
              renderValue={(value) => {
                if (value === 'campaign') return t('mailing.sends.campaigns')
                if (value === 'trigger') return t('mailing.sends.triggers')
                return t('mailing.sends.all_type')
              }}
              sx={{
                width: { xs: '100%', sm: 160 },
                height: 32,
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                color: '#656565',
                fontSize: 10,
                fontWeight: 500,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: 0,
                  px: 1.5
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#EFEFEF'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D7DAEA'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2570EB'
                }
              }}
            >
              <MenuItem value="all">{t('mailing.sends.all_type')}</MenuItem>
              <MenuItem value="campaign">{t('mailing.sends.campaigns')}</MenuItem>
              <MenuItem value="trigger">{t('mailing.sends.triggers')}</MenuItem>
            </MuiSelect>
          </Stack>
        )}

        <CountBadge
          count={emailSends.total}
          singularLabel="email"
          pluralLabel="emails"
        />
      </Stack>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        <ScrollTable
          height="100%"
          fetchNextPage={emailSends.fetchNextPage}
          shouldFetchNextPage={emailSends.shouldFetchNextPage}
          style={{ borderRadius: '8px' }}
        >
          {renderedTable}
        </ScrollTable>
      </Box>
    </Stack>
  )
}

export default SendsTable
